import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Navbar from './Navbar';
import Footer from './Footer';
import { trackLinkClick, trackPageView } from '../utils/tracking';
import { firestore } from '../firebase';

const BlogsPage = () => {
  const residentialCarouselRef = useRef(null);
  const commercialCarouselRef = useRef(null);
  const industrialCarouselRef = useRef(null);
  const [residentialScrollState, setResidentialScrollState] = useState({ canScrollLeft: false, canScrollRight: false });
  const [commercialScrollState, setCommercialScrollState] = useState({ canScrollLeft: false, canScrollRight: false });
  const [industrialScrollState, setIndustrialScrollState] = useState({ canScrollLeft: false, canScrollRight: false });
  const [firestoreBlogs, setFirestoreBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Hardcoded existing blogs
  const hardcodedBlogs = [
    {
      title: "7 Hidden Costs of NOT Going Solar in 2026 (Beyond Electricity Bills)",
      excerpt: "Discover 7 hidden costs of not going solar in 2026: productivity losses, diesel generator costs, carbon taxes, competitive disadvantage, ESG compliance pressure, rising tariffs, and higher insurance costs. Learn why delaying solar costs more than installing it today.",
      image: "/img/jp-valery-mwNkPv3d_EI-unsplash.jpg",
      date: "December 20, 2025",
      category: "Commercial Solar",
      author: "Lunar Electric",
      slug: "hidden-costs-not-going-solar-2026"
    },
    {
      title: "Solar EMI Financing for MSMEs: Complete guide to zero upfront cost in 2025",
      excerpt: "Complete guide to solar EMI financing for MSMEs in India. Learn about zero upfront cost solar installations, loan eligibility, benefits, ROI, and how to get started with solar energy for your business.",
      image: "/img/card2.png",
      date: "December 19, 2025",
      category: "Commercial Solar",
      author: "Lunar Electric",
      slug: "msme-solar-emi-financing"
    },
    {
      title: "Commercial Solar Depreciation & Tax Benefits in India",
      excerpt: "Break down accelerated depreciation, MAT planning, GST input credits, and compliance steps Indian businesses should know before installing commercial solar.",
      image: "/img/tax benefits solar.jpg",
      date: "November 9, 2025",
      category: "Commercial Solar",
      author: "Lunar Electric",
      slug: "commercial-solar-tax-benefits"
    },
    {
      title: "MC4 Connector Guide: What They Are, Why They Matter & How to Choose",
      excerpt: "Complete guide to MC4 connectors for solar panels. Learn what MC4 connectors are, why they're important for residential and commercial solar systems, and how to choose the right ones.",
      image: "/img/mc4 connector.png",
      date: "November 1, 2025",
      category: "Installation Guide",
      author: "Lunar Electric",
      slug: "mc4-connector-guide"
    },
    {
      title: "Top 10 Benefits of Solar Panels for Indian Homes",
      excerpt: "Discover the top 10 benefits of installing solar panels for Indian homes. Learn about cost savings, government subsidies, environmental benefits, and more.",
      image: "/img/benefits.jpg",
      date: "October 25, 2025",
      category: "Solar Benefits",
      author: "Lunar Electric",
      slug: "solar-panel-benefits"
    },
    {
      title: "5 Solar Procurement Models for Indian Businesses: CAPEX vs OPEX Guide",
      excerpt: "Complete guide to 5 solar procurement models for Indian businesses: CAPEX, OPEX, RESCO, Group Captive, and Third Party PPA. Choose the right model for your needs.",
      image: "/img/card3.png",
      date: "January 27, 2025",
      category: "Commercial Solar",
      author: "Lunar Electric",
      slug: "solar-models-explained"
    },
    {
      title: "Why Businesses Should Adopt Solar in India: Complete Guide",
      excerpt: "Discover why businesses should adopt solar energy in India. Learn about cost savings, government incentives, tax benefits, and ROI for commercial solar installations.",
      image: "/img/benefit for commercial.jpg",
      date: "January 26, 2025",
      category: "Commercial Solar",
      author: "Lunar Electric",
      slug: "commercial-solar-benefits"
    },
    {
      title: "Rooftop Solar in India: A Simple, Practical Guide for Homeowners",
      excerpt: "Complete step-by-step guide to installing rooftop solar at home. Learn about sizing, ALMM compliance, net metering, PM Surya Ghar subsidy, and more.",
      image: "/img/card1.png",
      date: "January 25, 2025",
      category: "Residential Rooftop Solar",
      author: "Lunar Electric",
      slug: "rooftop-solar-guide"
    }
  ];
  
  // Combine hardcoded blogs with Firestore blogs
  const blogs = [...firestoreBlogs, ...hardcodedBlogs];

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
    trackPageView('/blogs', 'Blogs Page');
  }, []);

  // Fetch NEW blogs from Firestore with REAL-TIME updates
  // Changes appear immediately without redeployment!
  useEffect(() => {
    // Public read - no authentication needed for published blogs
    const unsubscribe = firestore
      .collection('blogs')
      .where('published', '==', true)
      .onSnapshot(
        (snapshot) => {
          const blogData = snapshot.docs.map((doc) => {
            const data = doc.data();
            return {
              id: doc.id,
              title: data.title,
              subtitle: data.subtitle,
              excerpt: data.excerpt || data.subtitle,
              image: data.heroImage,
              date: data.createdAt?.toDate ? formatDate(data.createdAt.toDate()) : formatDate(data.createdAt),
              category: getCategoryFromSections(data.sections),
              sections: data.sections || [],
              author: data.author || 'Lunar Electric',
              slug: data.slug,
              body: data.body,
              createdAt: data.createdAt
            };
          });
          // Sort manually by createdAt (newest first)
          blogData.sort((a, b) => {
            const aTime = a.createdAt?.toMillis ? a.createdAt.toMillis() : (a.createdAt?.seconds * 1000) || 0;
            const bTime = b.createdAt?.toMillis ? b.createdAt.toMillis() : (b.createdAt?.seconds * 1000) || 0;
            return bTime - aTime;
          });
          setFirestoreBlogs(blogData);
          setLoading(false);
        },
        (error) => {
          console.error('Error fetching blogs:', error);
          setLoading(false);
        }
      );

    return () => unsubscribe();
  }, []);

  const formatDate = (date) => {
    if (!date) return '';
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getCategoryFromSections = (sections) => {
    if (!sections || sections.length === 0) return 'Solar Benefits';
    
    // If it has commercial, show as Commercial Solar
    if (sections.includes('commercial')) {
      return 'Commercial Solar';
    }
    // If it has residential, show as Residential Rooftop Solar
    if (sections.includes('residential')) {
      return 'Residential Rooftop Solar';
    }
    // If it has industrial, show as Industrial Solar
    if (sections.includes('industrial')) {
      return 'Industrial Solar';
    }
    return 'Solar Benefits';
  };

  const updateScrollState = (ref, setState) => {
    const node = ref.current;
    if (!node) {
      setState({ canScrollLeft: false, canScrollRight: false });
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = node;
    setState({
      canScrollLeft: scrollLeft > 0,
      canScrollRight: scrollLeft + clientWidth < scrollWidth - 1,
    });
  };

  const scrollCarousel = (ref, setState, direction) => {
    const node = ref.current;
    if (!node) {
      return;
    }

    const scrollAmount = node.clientWidth * 0.8;
    node.scrollBy({ left: direction * scrollAmount, behavior: 'smooth' });

    window.requestAnimationFrame(() => {
      updateScrollState(ref, setState);
    });
  };

  useEffect(() => {
    const mappings = [
      [residentialCarouselRef, setResidentialScrollState],
      [commercialCarouselRef, setCommercialScrollState],
      [industrialCarouselRef, setIndustrialScrollState],
    ];

    const handleResize = () => {
      mappings.forEach(([ref, setState]) => updateScrollState(ref, setState));
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    const listeners = mappings.map(([ref, setState]) => {
      const node = ref.current;
      if (!node) {
        return null;
      }

      const onScroll = () => updateScrollState(ref, setState);
      node.addEventListener('scroll', onScroll);
      return { node, onScroll };
    });

    return () => {
      window.removeEventListener('resize', handleResize);
      listeners.forEach((listener) => {
        if (listener && listener.node) {
          listener.node.removeEventListener('scroll', listener.onScroll);
        }
      });
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Lunar Electric Blogs | Solar Energy Tips & Industry Insights | Solar Blogs India</title>
        <meta name="description" content="Lunar Electric Blogs - Stay updated with the latest solar energy tips, industry news, and insights. Expert guides on residential and commercial solar installations in India." />
        <meta name="keywords" content="lunar electric blogs, solar blogs, solar energy blogs, solar panel blogs, rooftop solar blogs, solar blogs India, lunar electric solar blogs, residential solar blogs, commercial solar blogs" />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <meta property="og:title" content="Lunar Electric Blogs | Solar Energy Tips & Industry Insights" />
        <meta property="og:description" content="Lunar Electric Blogs - Stay updated with the latest solar energy tips, industry news, and insights. Expert guides on residential and commercial solar installations in India." />
        <meta property="og:url" content="https://lunarelectric.in/blogs" />
        <meta name="twitter:title" content="Lunar Electric Blogs | Solar Energy Tips & Industry Insights" />
        <meta name="twitter:description" content="Lunar Electric Blogs - Stay updated with the latest solar energy tips, industry news, and insights." />
        <meta name="twitter:url" content="https://lunarelectric.in/blogs" />
        <link rel="canonical" href="https://lunarelectric.in/blogs" />
        <link rel="alternate" type="application/rss+xml" title="Lunar Electric RSS Feed" href="https://lunarelectric.in/rss.xml" />
      </Helmet>
      <Navbar />
      <a href="#main-content" className="skip-to-main" style={{position: 'absolute', left: '-9999px', zIndex: 9999}} onFocus={(e) => e.target.style.left = '0'}>Skip to main content</a>
      <div className="blogs-page" id="main-content">
        <div className="blogs-hero">
          <div className="container">
            <div className="row">
              <div className="col-md-12 text-center">
                <h1 className="blogs-title">Solar Insights</h1>
                <p className="blogs-subtitle">Stay updated with the latest solar energy tips, industry news, and insights from Lunar Electric</p>
              </div>
            </div>
          </div>
        </div>

        <div className="blogs-content">
          <div className="container">
            <div className="blogs-section">
              <div className="blogs-header">
                <h2 className="blogs-section-title">Residential</h2>
                <a 
                  href="tel:+919148857417" 
                  className="view-all-link"
                  onClick={() => trackLinkClick('Call for More Info', 'tel:+919148857417', 'phone')}
                >
                  Call for More Info →
                </a>
              </div>
              
              <div className="blogs-carousel-wrapper">
                <button
                  type="button"
                  className="carousel-nav-button"
                  aria-label="Scroll residential blogs left"
                  onClick={() => scrollCarousel(residentialCarouselRef, setResidentialScrollState, -1)}
                  disabled={!residentialScrollState.canScrollLeft}
                >
                  <span aria-hidden="true">{'<'}</span>
                </button>
                <div className="blogs-carousel" ref={residentialCarouselRef}>
                  {blogs.filter(blog => 
                    blog.sections?.includes("residential") || 
                    blog.category === "Residential Rooftop Solar" || 
                    blog.category === "Solar Benefits" || 
                    blog.category === "Installation Guide"
                  ).map((blog, index) => (
                    <Link 
                      key={index} 
                      to={`/blogs/${blog.slug}`} 
                      className="blog-card-modern blog-card-link"
                      onClick={() => trackLinkClick(blog.title, `/blogs/${blog.slug}`, 'internal')}
                    >
                      <div className="blog-image-container">
                        <img src={blog.image} alt={blog.title} className="blog-card-image" />
                        <div className="blog-category-badge">{blog.category}</div>
                      </div>
                      <div className="blog-card-content">
                        <h3 className="blog-card-title">{blog.title}</h3>
                        <p className="blog-card-excerpt">{blog.excerpt}</p>
                        <div className="blog-read-link">Read More</div>
                        <div className="blog-card-footer">
                          <span className="blog-card-author">{blog.author}</span>
                          <span className="blog-card-date">{blog.date}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
                <button
                  type="button"
                  className="carousel-nav-button"
                  aria-label="Scroll residential blogs right"
                  onClick={() => scrollCarousel(residentialCarouselRef, setResidentialScrollState, 1)}
                  disabled={!residentialScrollState.canScrollRight}
                >
                  <span aria-hidden="true">{'>'}</span>
                </button>
              </div>
            </div>

            <div className="blogs-section">
              <div className="blogs-header">
                <h2 className="blogs-section-title">Commercial</h2>
                <a 
                  href="tel:+919148857417" 
                  className="view-all-link"
                  onClick={() => trackLinkClick('Call for More Info', 'tel:+919148857417', 'phone')}
                >
                  Call for More Info →
                </a>
              </div>
              
              <div className="blogs-carousel-wrapper">
                <button
                  type="button"
                  className="carousel-nav-button"
                  aria-label="Scroll commercial blogs left"
                  onClick={() => scrollCarousel(commercialCarouselRef, setCommercialScrollState, -1)}
                  disabled={!commercialScrollState.canScrollLeft}
                >
                  <span aria-hidden="true">{'<'}</span>
                </button>
                <div className="blogs-carousel" ref={commercialCarouselRef}>
                  {blogs.filter(blog => 
                    blog.sections?.includes("commercial") || 
                    blog.category === "Commercial Solar" || 
                    blog.category === "Installation Guide"
                  ).map((blog, index) => (
                    <Link 
                      key={index} 
                      to={`/blogs/${blog.slug}`} 
                      className="blog-card-modern blog-card-link"
                      onClick={() => trackLinkClick(blog.title, `/blogs/${blog.slug}`, 'internal')}
                    >
                      <div className="blog-image-container">
                        <img src={blog.image} alt={blog.title} className="blog-card-image" />
                        <div className="blog-category-badge">{blog.category}</div>
                      </div>
                      <div className="blog-card-content">
                        <h3 className="blog-card-title">{blog.title}</h3>
                        <p className="blog-card-excerpt">{blog.excerpt}</p>
                        <div className="blog-read-link">Read More</div>
                        <div className="blog-card-footer">
                          <span className="blog-card-author">{blog.author}</span>
                          <span className="blog-card-date">{blog.date}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
                <button
                  type="button"
                  className="carousel-nav-button"
                  aria-label="Scroll commercial blogs right"
                  onClick={() => scrollCarousel(commercialCarouselRef, setCommercialScrollState, 1)}
                  disabled={!commercialScrollState.canScrollRight}
                >
                  <span aria-hidden="true">{'>'}</span>
                </button>
              </div>
            </div>

            <div className="blogs-section">
              <div className="blogs-header">
                <h2 className="blogs-section-title">Industrial</h2>
                <a 
                  href="tel:+919148857417" 
                  className="view-all-link"
                  onClick={() => trackLinkClick('Call for More Info', 'tel:+919148857417', 'phone')}
                >
                  Call for More Info →
                </a>
              </div>
              
              <div className="blogs-carousel-wrapper">
                <button
                  type="button"
                  className="carousel-nav-button"
                  aria-label="Scroll industrial blogs left"
                  onClick={() => scrollCarousel(industrialCarouselRef, setIndustrialScrollState, -1)}
                  disabled={!industrialScrollState.canScrollLeft}
                >
                  <span aria-hidden="true">{'<'}</span>
                </button>
                <div className="blogs-carousel" ref={industrialCarouselRef}>
                  {blogs.filter(blog => 
                    blog.sections?.includes("industrial")
                  ).map((blog, index) => (
                    <Link 
                      key={index} 
                      to={`/blogs/${blog.slug}`} 
                      className="blog-card-modern blog-card-link"
                      onClick={() => trackLinkClick(blog.title, `/blogs/${blog.slug}`, 'internal')}
                    >
                      <div className="blog-image-container">
                        <img src={blog.image} alt={blog.title} className="blog-card-image" />
                        <div className="blog-category-badge">{blog.category}</div>
                      </div>
                      <div className="blog-card-content">
                        <h3 className="blog-card-title">{blog.title}</h3>
                        <p className="blog-card-excerpt">{blog.excerpt}</p>
                        <div className="blog-read-link">Read More</div>
                        <div className="blog-card-footer">
                          <span className="blog-card-author">{blog.author}</span>
                          <span className="blog-card-date">{blog.date}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
                <button
                  type="button"
                  className="carousel-nav-button"
                  aria-label="Scroll industrial blogs right"
                  onClick={() => scrollCarousel(industrialCarouselRef, setIndustrialScrollState, 1)}
                  disabled={!industrialScrollState.canScrollRight}
                >
                  <span aria-hidden="true">{'>'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="blogs-cta">
          <div className="container">
            <div className="row">
              <div className="col-md-12 text-center">
                <h2>Ready to Go Solar?</h2>
                <p>Get in touch for professional solar rooftop installations for your home or business</p>
                <Link 
                  to="/callback" 
                  className="btn btn-primary"
                  onClick={() => trackLinkClick('Get Callback', '/callback', 'internal')}
                >
                  Get Callback
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default BlogsPage;











